import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/feature/SEOHead';
import ExercicioGratisNavbar from '../components/ExercicioGratisNavbar';
import LeadCaptureForm from '../components/LeadCaptureForm';

export default function IdentidadeAltaPerformancePage() {
  const [currentIdentity, setCurrentIdentity] = useState<string[]>([]);
  const [desiredIdentity, setDesiredIdentity] = useState<string[]>([]);

  const identityTraits = [
    { id: 'confiante', label: 'Confiante', icon: 'ri-shield-star-line', color: 'bg-blue-100 text-blue-600' },
    { id: 'disciplinado', label: 'Disciplinado', icon: 'ri-calendar-check-line', color: 'bg-indigo-100 text-indigo-600' },
    { id: 'focado', label: 'Focado', icon: 'ri-focus-3-line', color: 'bg-purple-100 text-purple-600' },
    { id: 'resiliente', label: 'Resiliente', icon: 'ri-shield-line', color: 'bg-violet-100 text-violet-600' },
    { id: 'produtivo', label: 'Produtivo', icon: 'ri-rocket-line', color: 'bg-pink-100 text-pink-600' },
    { id: 'lider', label: 'Líder', icon: 'ri-team-line', color: 'bg-orange-100 text-orange-600' },
    { id: 'estrategico', label: 'Estratégico', icon: 'ri-lightbulb-line', color: 'bg-amber-100 text-amber-600' },
    { id: 'equilibrado', label: 'Equilibrado', icon: 'ri-scales-3-line', color: 'bg-green-100 text-green-600' },
  ];

  const toggleCurrent = (id: string) => setCurrentIdentity(prev => prev.includes(id) ? prev.filter(i => i !== id) : prev.length < 5 ? [...prev, id] : prev);
  const toggleDesired = (id: string) => setDesiredIdentity(prev => prev.includes(id) ? prev.filter(i => i !== id) : prev.length < 5 ? [...prev, id] : prev);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50">
      <SEOHead
        title="Identidade de Alta Performance Grátis | PNL — Carta do Eu Futuro"
        description="Experimente gratuitamente o exercício de Identidade de Alta Performance. Transforme sua identidade e torne-se a versão que alcança resultados extraordinários com PNL."
        keywords="identidade alta performance, PNL grátis, transformação pessoal, alta performance"
        canonical="/exercicio-gratis/identidade-alta-performance"
      />
      <ExercicioGratisNavbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-4 overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10 text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-sm font-semibold mb-6"><i className="ri-gift-line"></i>VERSÃO GRATUITA</div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-slate-700 via-gray-700 to-zinc-700 bg-clip-text text-transparent leading-tight">Identidade de Alta Performance</h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">Transforme sua identidade e torne-se a versão de alta performance que alcança resultados extraordinários</p>
        </div>
      </section>

      {/* O que é */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">O que é a Identidade de Alta Performance?</h2>
          <p className="text-lg leading-relaxed mb-6 text-gray-700">A <strong>Identidade de Alta Performance</strong> é uma técnica avançada de PNL que trabalha no nível mais profundo da mudança: sua identidade. Não se trata apenas de mudar comportamentos, mas de transformar quem você é.</p>
          <p className="text-lg leading-relaxed text-gray-700">Quando você muda sua identidade, tudo muda automaticamente: suas crenças, valores, comportamentos e resultados.</p>
        </div>
      </section>

      {/* Exercício */}
      <section className="py-16 px-4 bg-gradient-to-br from-slate-50 to-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 md:p-12">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-sm font-semibold mb-4"><i className="ri-gift-line"></i>EXPERIMENTE GRÁTIS</div>
              <h2 className="text-3xl font-bold mb-4 text-gray-900">Exercício Introdutório</h2>
            </div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Como você se vê HOJE? (até 5)</h3>
            <div className="grid md:grid-cols-4 gap-3 mb-8">
              {identityTraits.map((trait) => (
                <button key={trait.id} onClick={() => toggleCurrent(trait.id)} disabled={!currentIdentity.includes(trait.id) && currentIdentity.length >= 5} className={`p-3 rounded-xl border-2 transition-all text-left cursor-pointer ${currentIdentity.includes(trait.id) ? 'border-slate-500 bg-slate-50 scale-105' : currentIdentity.length >= 5 ? 'opacity-50 cursor-not-allowed border-gray-200' : 'border-gray-200 hover:border-slate-300'}`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 ${trait.color} rounded-lg flex items-center justify-center`}><i className={`${trait.icon} text-lg`}></i></div>
                    <span className="font-semibold text-gray-900 text-sm">{trait.label}</span>
                  </div>
                </button>
              ))}
            </div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Como você quer SE TORNAR? (até 5)</h3>
            <div className="grid md:grid-cols-4 gap-3 mb-8">
              {identityTraits.map((trait) => (
                <button key={trait.id} onClick={() => toggleDesired(trait.id)} disabled={!desiredIdentity.includes(trait.id) && desiredIdentity.length >= 5} className={`p-3 rounded-xl border-2 transition-all text-left cursor-pointer ${desiredIdentity.includes(trait.id) ? 'border-blue-500 bg-blue-50 scale-105' : desiredIdentity.length >= 5 ? 'opacity-50 cursor-not-allowed border-gray-200' : 'border-gray-200 hover:border-blue-300'}`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 ${trait.color} rounded-lg flex items-center justify-center`}><i className={`${trait.icon} text-lg`}></i></div>
                    <span className="font-semibold text-gray-900 text-sm">{trait.label}</span>
                  </div>
                </button>
              ))}
            </div>
            {/* Bloqueados */}
            <div className="relative mt-6">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/60 to-white backdrop-blur-sm z-10 rounded-2xl flex items-center justify-center">
                <div className="bg-gradient-to-r from-slate-700 via-gray-700 to-zinc-700 text-white p-8 rounded-2xl text-center max-w-sm">
                  <i className="ri-vip-crown-line text-5xl mb-4 block"></i>
                  <h3 className="text-2xl font-bold mb-3">Desbloqueie o Exercício Completo</h3>
                  <p className="text-slate-200 mb-6">7 passos completos + áudio guiado + workbook</p>
                  <Link to="/planos" className="bg-white text-slate-700 px-8 py-4 rounded-xl font-bold hover:bg-slate-50 transition-all whitespace-nowrap block">Ver Planos — A partir de R$ 18</Link>
                </div>
              </div>
              <div className="opacity-30 pointer-events-none space-y-4">
                {[3,4,5,6,7].map((n) => <div key={n} className="p-6 bg-gray-50 rounded-xl border-2 border-gray-200"><h3 className="font-bold text-gray-600">Passo {n} — bloqueado</h3></div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <LeadCaptureForm exercicioNome="Identidade de Alta Performance (PNL)" />

      {/* CTA Final */}
      <section className="py-20 px-4 bg-gradient-to-br from-charcoal-950 via-charcoal-900 to-charcoal-950 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-12 h-px bg-gold-500/30 mx-auto mb-8"></div>
          <i className="ri-user-star-fill text-5xl mb-6 opacity-60 block text-gold-400"></i>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-6 text-white">Pronto para Transformar Sua Identidade?</h2>
          <p className="text-xl text-white/55 mb-8 max-w-2xl mx-auto font-['Montserrat'] font-light">Desbloqueie o exercício completo e comece sua jornada de alta performance hoje mesmo</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/planos" className="bg-gradient-to-r from-gold-500 to-gold-700 text-charcoal-900 px-10 py-5 rounded-xl font-bold hover:from-gold-600 hover:to-gold-800 transition-all whitespace-nowrap font-['Montserrat'] tracking-wide">Ver Planos e Preços</Link>
            <Link to="/" className="bg-white/8 text-white px-10 py-5 rounded-xl font-bold hover:bg-white/14 transition-all border border-gold-500/20 whitespace-nowrap font-['Montserrat'] tracking-wide">Voltar ao Início</Link>
          </div>
          <p className="text-white/30 text-sm mt-6 font-['Montserrat']">Garantia de 7 dias · Acesso vitalício · Suporte incluído</p>
        </div>
      </section>
    </div>
  );
}
